import React, { useEffect, useState, useCallback } from 'react';
import * as mentorApi from '../api/mentorApi';

/**
 * Reports & Insights — role-scoped performance dashboard.
 *
 * SPOC is never a role this screen serves — SPOCs are school staff outside
 * IFSPD's own ecosystem and are never scored. Program Administrators view
 * aggregate Counselor/Auditor screens; Counselors/Auditors see only their
 * own self-scorecard. The performance score (historically "composite") and
 * its formula live entirely on the backend (CompositeScoreCalculator) — this
 * screen only renders what the API returns, it never recomputes scoring math.
 */

function performanceScoreLabel(role) {
  return role === 'counselor' ? 'Counsellor Performance Score' : 'Auditor Performance Score';
}

function avgPerformanceScoreLabel(role) {
  return role === 'counselor' ? 'Avg. Counsellor Performance Score' : 'Avg. Auditor Performance Score';
}

function scoreValue(row) {
  if (!row) return 0;
  return row.performanceScore ?? row.composite ?? 0;
}

const BAND_ORDER = ['Exceptional', 'Strong', 'On Track', 'Needs Support'];
const PERIOD_LABELS = { fortnight: 'this fortnight', quarter: 'this quarter', program: 'the program to date' };

function BandPill({ band }) {
  if (!band) return null;
  return (
    <span style={{
      fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 14,
      background: band.bg, color: band.fg,
    }}>
      {band.label}
    </span>
  );
}

function AttributeBar({ label, weight, value, benchmark }) {
  const notApplicable = value == null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
        <span style={{ color: '#1A1F2B' }}>
          {label} {weight != null && <span style={{ color: '#9CA3AF', fontWeight: 500 }}>({weight}%)</span>}
        </span>
        <span style={{ color: notApplicable ? '#9CA3AF' : '#0F2557', fontWeight: 700 }}>
          {notApplicable ? 'No activity yet' : value}
        </span>
      </div>
      <div style={{ height: 10, background: '#F1F2F4', borderRadius: 5, overflow: 'hidden', position: 'relative' }}>
        {!notApplicable && (
          <div style={{ height: 10, borderRadius: 5, background: '#1E3A8A', width: `${Math.min(100, value)}%` }} />
        )}
        {!notApplicable && benchmark != null && (
          <div style={{ position: 'absolute', top: -2, left: `${benchmark}%`, width: 2, height: 14, background: '#9CA3AF' }} />
        )}
      </div>
    </div>
  );
}

function CompositeRing({ composite, caption = 'SCORE' }) {
  const pct = Math.min(100, composite || 0);
  return (
    <div style={{
      width: 120, height: 120, borderRadius: '50%',
      background: `conic-gradient(#1E3A8A ${pct * 3.6}deg, #E5E7EB 0deg)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{ width: 92, height: 92, borderRadius: '50%', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: '#0F2557' }}>{composite}</div>
        <div style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 700, letterSpacing: 0.5 }}>{caption}</div>
      </div>
    </div>
  );
}

export default function ReportsInsights({ currentUser }) {
  const isAdmin = currentUser?.role === 'program_administrator';
  const [entityType, setEntityType] = useState('auditor'); // 'auditor' | 'counselor' — admin only
  const [screen, setScreen] = useState('overview'); // overview | leaderboard | profile
  const [selectedId, setSelectedId] = useState(null);
  const [period, setPeriod] = useState('quarter');
  const [reportOpen, setReportOpen] = useState(false);

  const [overview, setOverview] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [profile, setProfile] = useState(null);
  const [selfProfile, setSelfProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Wave 3 — overview KPI/attribute drill into leaderboard with sort/filter chip
  const [drill, setDrill] = useState(null); // { type: 'composite'|'attribute'|'filter', key?, label?, min?, max? }

  const role = isAdmin ? entityType : currentUser?.role;

  const loadOverview = useCallback(() => {
    setLoading(true);
    setError(null);
    // Prefetch leaderboard with overview so KPI/attribute click-through is instant (CR-mentor-reports-clickthrough-01).
    Promise.all([
      mentorApi.getReportsOverview(role, period),
      mentorApi.getReportsLeaderboard(role, period).catch((e) => {
        console.error('Reports leaderboard prefetch failed:', e);
        return null;
      }),
    ])
      .then(([ov, lb]) => {
        setOverview(ov);
        if (lb) setLeaderboard(lb);
      })
      .catch((e) => { console.error('Reports overview failed:', e); setError('Could not load overview.'); })
      .finally(() => setLoading(false));
  }, [role, period]);

  const loadLeaderboard = useCallback(() => {
    setLoading(true);
    setError(null);
    mentorApi.getReportsLeaderboard(role, period)
      .then(setLeaderboard)
      .catch((e) => { console.error('Reports leaderboard failed:', e); setError('Could not load leaderboard.'); })
      .finally(() => setLoading(false));
  }, [role, period]);

  const loadProfile = useCallback((id) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    mentorApi.getReportsProfile(role, id, period)
      .then(setProfile)
      .catch((e) => { console.error('Reports profile failed:', e); setError('Could not load profile.'); })
      .finally(() => setLoading(false));
  }, [role, period]);

  const loadSelf = useCallback(() => {
    setLoading(true);
    setError(null);
    mentorApi.getReportsSelf(period)
      .then(setSelfProfile)
      .catch((e) => { console.error('Reports self failed:', e); setError('Could not load your scorecard.'); })
      .finally(() => setLoading(false));
  }, [period]);

  useEffect(() => {
    if (!isAdmin) { loadSelf(); return; }
    if (screen === 'overview') loadOverview();
    else if (screen === 'leaderboard') {
      // Keep existing leaderboard if prefetched for same role/period; still refresh for freshness.
      loadLeaderboard();
    } else if (screen === 'profile') loadProfile(selectedId);
  }, [isAdmin, screen, selectedId, loadOverview, loadLeaderboard, loadProfile, loadSelf]);

  useEffect(() => {
    // Switching entity/period on the leaderboard also gives us a default selected profile.
    if (isAdmin && screen === 'leaderboard' && leaderboard?.rows?.length && !selectedId) {
      setSelectedId(leaderboard.rows[0].id);
    }
  }, [isAdmin, screen, leaderboard, selectedId]);

  useEffect(() => {
    // Entity or period change clears drill context.
    setDrill(null);
  }, [entityType, period]);

  const openDrill = useCallback((nextDrill) => {
    setDrill(nextDrill);
    setScreen('leaderboard');
    if (!leaderboard) {
      loadLeaderboard();
    }
  }, [leaderboard, loadLeaderboard]);

  const leaderboardRowsForDisplay = (() => {
    const rows = leaderboard?.rows || [];
    if (!drill) return rows;

    let filtered = rows;
    if (drill.type === 'filter') {
      if (drill.min != null) filtered = filtered.filter((p) => (scoreValue(p) ?? -1) >= drill.min);
      if (drill.max != null) filtered = filtered.filter((p) => (scoreValue(p) ?? 999) < drill.max);
    }

    if (drill.type === 'attribute' && drill.key) {
      return [...filtered].sort((a, b) => {
        const av = a.attributeScores?.[drill.key];
        const bv = b.attributeScores?.[drill.key];
        const aNull = av == null;
        const bNull = bv == null;
        if (aNull && bNull) return scoreValue(b) - scoreValue(a);
        if (aNull) return 1; // nulls last
        if (bNull) return -1;
        if (bv !== av) return bv - av;
        return scoreValue(b) - scoreValue(a);
      });
    }

    // composite / filter → performance score desc
    return [...filtered].sort((a, b) => scoreValue(b) - scoreValue(a));
  })();

  if (loading && !overview && !leaderboard && !profile && !selfProfile) {
    return <div style={{ padding: 24, color: '#6B7280' }}>Loading Reports & Insights…</div>;
  }
  if (error) {
    return <div style={{ padding: 24, color: '#B91C1C' }}>{error}</div>;
  }

  const entityLabel = role === 'counselor' ? 'Counselors' : 'Auditors';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.6, color: '#F5941F', textTransform: 'uppercase', marginBottom: 6 }}>Reports &amp; Insights</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#0F2557', letterSpacing: -0.4 }}>
            {isAdmin ? `${entityLabel} Performance` : 'My Performance'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            style={{ fontSize: 13, fontWeight: 600, color: '#1A1F2B', border: '1px solid #D1D5DB', padding: '9px 14px', borderRadius: 8, background: '#fff' }}
          >
            <option value="fortnight">This Fortnight</option>
            <option value="quarter">This Quarter</option>
            <option value="program">Program-to-Date</option>
          </select>
          <button
            onClick={() => {
              // Leaderboard's PDF context is the aggregate report — same shape as Overview.
              // Fetch it lazily here if the user hasn't visited the Overview tab yet this session.
              if (isAdmin && screen === 'leaderboard' && !overview) {
                loadOverview();
              }
              setReportOpen(true);
            }}
            style={{ fontSize: 13, fontWeight: 700, color: '#fff', background: '#F5941F', padding: '9px 18px', borderRadius: 8, border: 'none', cursor: 'pointer' }}
          >
            ⭳ Download Report
          </button>
        </div>
      </div>

      {isAdmin && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, borderBottom: '1px solid #E5E7EB', marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 28 }}>
            {['overview', 'leaderboard', 'profile'].map((s) => (
              <div
                key={s}
                onClick={() => setScreen(s)}
                style={{
                  padding: '14px 2px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  color: screen === s ? '#0F2557' : '#9CA3AF',
                  borderBottom: `3px solid ${screen === s ? '#F5941F' : 'transparent'}`,
                  textTransform: 'capitalize',
                }}
              >
                {s === 'profile' ? 'Individual Profile' : s}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', background: '#EEF0F3', borderRadius: 8, padding: 3, gap: 2, marginBottom: 10 }}>
            {['auditor', 'counselor'].map((e) => (
              <button
                key={e}
                onClick={() => { setEntityType(e); setSelectedId(null); }}
                style={{
                  flex: 1, textAlign: 'center', fontSize: 12, fontWeight: 700, padding: '7px 14px', borderRadius: 6,
                  cursor: 'pointer', border: 'none', outline: 'none',
                  background: entityType === e ? '#fff' : 'transparent',
                  color: entityType === e ? '#0F2557' : '#6B7280',
                  boxShadow: entityType === e ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                {e === 'counselor' ? 'Counselors' : 'Auditors'}
              </button>
            ))}
          </div>
        </div>
      )}

      {isAdmin && screen === 'overview' && overview && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 20, marginBottom: 32 }}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => openDrill({ type: 'composite', label: `All ${entityLabel} · ${performanceScoreLabel(role)}` })}
              onKeyDown={(e) => { if (e.key === 'Enter') openDrill({ type: 'composite', label: `All ${entityLabel} · ${performanceScoreLabel(role)}` }); }}
              style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '20px 22px', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#93B4E8'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; }}
              title="View ranked list"
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>Total {entityLabel}</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#0F2557', marginTop: 8 }}>{overview.total}</div>
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={() => openDrill({ type: 'composite', label: `${avgPerformanceScoreLabel(role)} · ranked` })}
              style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '20px 22px', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#93B4E8'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; }}
              title="View ranked list"
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>{avgPerformanceScoreLabel(role)}</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#0F2557', marginTop: 8 }}>{overview.avgPerformanceScore ?? overview.avgComposite}</div>
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={() => openDrill({ type: 'filter', min: 78, label: 'Strong or Exceptional (≥78)' })}
              style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '20px 22px', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#93B4E8'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; }}
              title="Filter composite ≥ 78"
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>Strong or Exceptional</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#0F2557', marginTop: 8 }}>{overview.strongOrExceptionalPct}%</div>
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={() => openDrill({ type: 'filter', max: 65, label: 'Needs Support (<65)' })}
              style={{ background: '#fff', border: '1px solid #F5CDA0', borderRadius: 10, padding: '20px 22px', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#F5941F'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#F5CDA0'; }}
              title="Filter composite < 65"
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: '#B3560E', textTransform: 'uppercase' }}>Needs Support</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#B3560E', marginTop: 8 }}>{overview.needsSupportCount}</div>
            </div>
          </div>

          <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '24px 26px', marginBottom: 28 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0F2557', marginBottom: 4 }}>Attribute Averages</div>
            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 18 }}>Click an attribute to rank people by that score · weight in parentheses</div>
            {(overview.attributeAverages || []).map((row) => (
              <div
                key={row.key}
                role="button"
                tabIndex={0}
                onClick={() => openDrill({ type: 'attribute', key: row.key, label: row.label })}
                onKeyDown={(e) => { if (e.key === 'Enter') openDrill({ type: 'attribute', key: row.key, label: row.label }); }}
                style={{ cursor: 'pointer', borderRadius: 8, padding: '4px 6px', margin: '0 -6px' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#F7F8FA'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                title={`Rank by ${row.label}`}
              >
                <AttributeBar label={row.label} weight={row.weight} value={row.value} />
              </div>
            ))}
          </div>

          <div style={{ background: '#fff', border: '1px solid #F5CDA0', borderRadius: 10, padding: '24px 26px' }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#B3560E', marginBottom: 14 }}>Needs Attention</div>
            {(overview.flagged || []).length === 0 && (
              <div style={{ fontSize: 13, color: '#6B7280' }}>No one is flagged this period.</div>
            )}
            {(overview.flagged || []).map((p) => (
              <div
                key={p.id}
                onClick={() => { setEntityType(role); setSelectedId(p.id); setScreen('profile'); }}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #FCE4CC', cursor: 'pointer' }}
              >
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1A1F2B' }}>{p.name}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <BandPill band={p.band} />
                  <span style={{ fontSize: 18, fontWeight: 800, color: '#0F2557' }}>{scoreValue(p)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isAdmin && screen === 'leaderboard' && leaderboard && (
        <div>
          {drill && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: 12, fontWeight: 700, color: '#0F2557', background: '#EEF4FF',
                border: '1px solid #C7D6F2', borderRadius: 16, padding: '6px 12px',
              }}>
                {drill.type === 'attribute' ? `Sorted by ${drill.label}` : drill.label}
              </span>
              <button
                type="button"
                onClick={() => setDrill(null)}
                style={{
                  fontSize: 12, fontWeight: 700, color: '#4B5563', background: '#F1F2F4',
                  border: 'none', borderRadius: 16, padding: '6px 12px', cursor: 'pointer',
                }}
              >
                Clear filter
              </button>
            </div>
          )}
          <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: drill?.type === 'attribute' ? '44px 1.6fr 0.9fr 0.9fr 0.9fr' : '44px 1.6fr 0.9fr 0.9fr',
              gap: 12, padding: '14px 22px', background: '#F7F8FA', borderBottom: '1px solid #E5E7EB',
              fontSize: 11, fontWeight: 800, color: '#6B7280', textTransform: 'uppercase',
            }}>
              <span>Rank</span><span>Name</span>
              {drill?.type === 'attribute' && <span>{drill.label || 'Attribute'}</span>}
              <span>Score</span><span>Band</span>
            </div>
            {leaderboardRowsForDisplay.length === 0 && (
              <div style={{ padding: '20px 22px', fontSize: 13, color: '#6B7280' }}>No people match this filter.</div>
            )}
            {leaderboardRowsForDisplay.map((p, idx) => (
              <div
                key={p.id}
                onClick={() => { setSelectedId(p.id); setScreen('profile'); }}
                style={{
                  display: 'grid',
                  gridTemplateColumns: drill?.type === 'attribute' ? '44px 1.6fr 0.9fr 0.9fr 0.9fr' : '44px 1.6fr 0.9fr 0.9fr',
                  gap: 12, padding: '16px 22px', alignItems: 'center', borderBottom: '1px solid #F1F2F4', cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 15, fontWeight: 800, color: '#9CA3AF' }}>{drill ? idx + 1 : p.rank}</span>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1A1F2B' }}>{p.name}</div>
                {drill?.type === 'attribute' && (
                  <span style={{ fontSize: 16, fontWeight: 800, color: '#0F2557' }}>
                    {p.attributeScores?.[drill.key] == null ? '—' : p.attributeScores[drill.key]}
                  </span>
                )}
                <span style={{ fontSize: 18, fontWeight: 800, color: '#0F2557' }}>{scoreValue(p)}</span>
                <BandPill band={p.band} />
              </div>
            ))}
          </div>
        </div>
      )}

      {isAdmin && screen === 'profile' && profile && (
        <ProfileBody data={profile} attributeLabels={BAND_ORDER} />
      )}

      {!isAdmin && selfProfile && (
        <ProfileBody data={selfProfile} self selfName={currentUser?.name} />
      )}

      {reportOpen && (
        <ReportOverlay
          isAdmin={isAdmin}
          entityLabel={entityLabel}
          period={period}
          data={isAdmin ? (screen === 'profile' ? profile : overview) : selfProfile}
          selfName={currentUser?.name}
          showLeaderboard={isAdmin && screen !== 'profile' && !!overview}
          leaderboardRows={leaderboard?.rows}
          onClose={() => setReportOpen(false)}
        />
      )}
    </div>
  );
}

function ReportOverlay({ isAdmin, entityLabel, period, data, selfName, showLeaderboard, leaderboardRows, onClose }) {
  if (!data) return null;

  const genDate = 'Generated ' + new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const isAggregate = isAdmin && data.total != null;
  const roleKey = data.role || (entityLabel === 'Counselors' ? 'counselor' : 'auditor');
  const scoreName = data.scoreLabel || performanceScoreLabel(roleKey);
  const avgScoreName = avgPerformanceScoreLabel(roleKey);
  const avgScore = data.avgPerformanceScore ?? data.avgComposite;

  let scopeLabel, subtitle, narrative, kpis, attributeRows;

  if (isAggregate) {
    scopeLabel = `${entityLabel} — Aggregate Performance Report`;
    subtitle = `Program-wide ${entityLabel.toLowerCase()} scorecard · ${PERIOD_LABELS[period]} · ${genDate}`;
    narrative = `Across ${data.total} ${entityLabel.toLowerCase()}, the average ${scoreName.toLowerCase()} is ${avgScore}, with ${data.strongOrExceptionalPct}% rated "Strong" or above and ${data.needsSupportCount} flagged as "Needs Support" ${PERIOD_LABELS[period]}.`;
    kpis = [
      { label: `Total ${entityLabel}`, value: data.total },
      { label: avgScoreName, value: avgScore },
      { label: 'Strong or Exceptional', value: `${data.strongOrExceptionalPct}%` },
      { label: 'Needs Support', value: data.needsSupportCount },
    ];
    attributeRows = data.attributeAverages || [];
  } else {
    // Self-view's own name never comes from the roster lookup (Ritika's roster
    // endpoints are caller-scoped and exclude the caller's own row) — use the
    // logged-in user's own name instead of falling back to a bare id.
    const displayName = isAdmin ? (data.name || 'Individual') : (selfName || data.name || 'Individual');
    scopeLabel = `${displayName} — Performance Report`;
    subtitle = `${data.role === 'counselor' ? 'Counselor' : 'Auditor'} ${isAdmin ? 'profile' : 'self-report'} · ${PERIOD_LABELS[period]} · ${genDate}`;
    narrative = data.rank != null
      ? `${displayName} is ranked ${data.rank} of ${data.totalInRole}, in the "${data.band?.label}" band ${PERIOD_LABELS[period]}.`
      : `${displayName} is in the "${data.band?.label}" band ${PERIOD_LABELS[period]}.`;
    kpis = [
      { label: scoreName, value: scoreValue(data) },
      { label: 'Band', value: data.band?.label },
      { label: 'Rank', value: data.rank != null ? `${data.rank} of ${data.totalInRole}` : '—' },
    ];
    if (data.role === 'auditor' && data.sessionVolume) {
      kpis.push({ label: 'Sessions (period)', value: data.sessionVolume.total ?? 0 });
    }
    attributeRows = Object.entries(data.attributeScores || {}).map(([key, value]) => ({ key, label: key.replace(/_/g, ' '), value }));
  }

  return (
    <div className="report-overlay" style={{ position: 'fixed', inset: 0, background: '#F1F2F4', zIndex: 50, overflow: 'auto' }}>
      <div className="no-print" style={{ position: 'sticky', top: 0, display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 32px', background: '#fff', borderBottom: '1px solid #E5E7EB', zIndex: 5 }}>
        <button onClick={onClose} style={{ fontSize: 13, fontWeight: 700, color: '#4B5563', background: '#F1F2F4', border: 'none', padding: '10px 18px', borderRadius: 8, cursor: 'pointer' }}>Close</button>
        <button onClick={() => window.print()} style={{ fontSize: 13, fontWeight: 700, color: '#fff', background: '#0F2557', border: 'none', padding: '10px 18px', borderRadius: 8, cursor: 'pointer' }}>Print / Save as PDF</button>
      </div>

      <div style={{ maxWidth: 900, margin: '32px auto', display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div className="report-page" style={{ background: '#0F2557', borderRadius: 6, padding: '100px 70px', minHeight: 640, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: 2, color: '#F5941F', textTransform: 'uppercase' }}>IFSPD · Glosco Fellowship</div>
            <div style={{ height: 3, width: 60, background: '#F5941F', margin: '18px 0 36px' }} />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1.5, color: '#93B4E8', textTransform: 'uppercase', marginBottom: 14 }}>Reports &amp; Insights</div>
            <div style={{ fontSize: 42, fontWeight: 800, color: '#fff', lineHeight: 1.15, maxWidth: 600 }}>{scopeLabel}</div>
            <div style={{ fontSize: 16, color: '#C7D6F2', marginTop: 20 }}>{subtitle}</div>
          </div>
          <div style={{ fontSize: 12, color: '#93B4E8' }}>Prepared for internal program review · Confidential</div>
        </div>

        <div className="report-page" style={{ background: '#fff', borderRadius: 6, padding: '60px 70px', minHeight: 640 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.5, color: '#F5941F', textTransform: 'uppercase', marginBottom: 8 }}>Executive Summary</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#0F2557', marginBottom: 30 }}>{scopeLabel}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 20, marginBottom: 36 }}>
            {kpis.map((k) => (
              <div key={k.label} style={{ borderTop: '3px solid #0F2557', paddingTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>{k.label}</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: '#0F2557', marginTop: 6 }}>{k.value}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, borderLeft: '3px solid #F5941F', paddingLeft: 18 }}>{narrative}</div>
        </div>

        <div className="report-page" style={{ background: '#fff', borderRadius: 6, padding: '60px 70px', minHeight: 640 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.5, color: '#F5941F', textTransform: 'uppercase', marginBottom: 8 }}>Attribute Performance</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#0F2557', marginBottom: 30 }}>Scorecard Breakdown</div>
          {attributeRows.map((row) => (
            <AttributeBar key={row.key} label={row.label} weight={row.weight} value={row.value} />
          ))}
        </div>

        {showLeaderboard && leaderboardRows && (
          <div className="report-page" style={{ background: '#fff', borderRadius: 6, padding: '60px 70px', minHeight: 640 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.5, color: '#F5941F', textTransform: 'uppercase', marginBottom: 8 }}>Appendix</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#0F2557', marginBottom: 30 }}>Ranked Leaderboard</div>
            <div style={{ display: 'grid', gridTemplateColumns: '44px 1.6fr 0.9fr 0.9fr', gap: 12, padding: '12px 0', borderBottom: '2px solid #0F2557', fontSize: 11, fontWeight: 800, color: '#6B7280', textTransform: 'uppercase' }}>
              <span>Rank</span><span>Name</span><span>Score</span><span>Band</span>
            </div>
            {leaderboardRows.map((p) => (
              <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '44px 1.6fr 0.9fr 0.9fr', gap: 12, padding: '12px 0', alignItems: 'center', borderBottom: '1px solid #F1F2F4' }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#9CA3AF' }}>{p.rank}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#1A1F2B' }}>{p.name}</span>
                <span style={{ fontSize: 15, fontWeight: 800, color: '#0F2557' }}>{scoreValue(p)}</span>
                <BandPill band={p.band} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileBody({ data, self, selfName }) {
  const attrs = Object.entries(data.attributeScores || {});
  const teamAvg = data.teamAverage || {};
  // Self-view's own name never comes from the roster lookup (structurally can't —
  // Ritika's roster endpoints are caller-scoped and exclude the caller's own row).
  // Use the logged-in user's own name instead of falling back to a bare id.
  const displayName = self ? (selfName || data.name) : (data.name || `#${data.id}`);
  const volume = data.role === 'auditor' ? data.sessionVolume : null;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 260px', gap: 24, marginBottom: 24 }}>
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '26px 28px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#0F2557' }}>{displayName}</div>
              {data.rank != null && (
                <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>Rank {data.rank} of {data.totalInRole}</div>
              )}
              {volume && (
                <div style={{ fontSize: 13, color: '#0F2557', marginTop: 10, fontWeight: 700 }}>
                  Sessions this period: <span style={{ fontSize: 18 }}>{volume.total ?? 0}</span>
                  <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, marginLeft: 8 }}>accepted · non-cancelled</span>
                </div>
              )}
            </div>
            <BandPill band={data.band} />
          </div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CompositeRing composite={scoreValue(data)} caption="SCORE" />
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '24px 26px', marginBottom: volume ? 24 : 0 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#0F2557', marginBottom: 18 }}>
          {self ? 'My Attribute Breakdown' : 'Attribute Breakdown'}
        </div>
        {attrs.map(([key, value]) => (
          <AttributeBar key={key} label={key.replace(/_/g, ' ')} value={value} benchmark={teamAvg[key]} />
        ))}
        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 16 }}>Grey marker = team average for this attribute</div>
      </div>

      {volume && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <VolumeTable
            title="By week × school"
            columns={['Week', 'School', 'Count']}
            rows={(volume.byWeek || []).map((r) => [r.week, r.schoolName || r.schoolId || '—', r.count])}
          />
          <VolumeTable
            title="By month × school"
            columns={['Month', 'School', 'Count']}
            rows={(volume.byMonth || []).map((r) => [r.month, r.schoolName || r.schoolId || '—', r.count])}
          />
        </div>
      )}
    </div>
  );
}

function VolumeTable({ title, columns, rows }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '20px 22px' }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: '#0F2557', marginBottom: 12 }}>{title}</div>
      {rows.length === 0 ? (
        <div style={{ fontSize: 13, color: '#6B7280' }}>No accepted sessions in this period.</div>
      ) : (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: '0.9fr 1.4fr 0.5fr', gap: 8,
            fontSize: 11, fontWeight: 800, color: '#6B7280', textTransform: 'uppercase',
            paddingBottom: 8, borderBottom: '1px solid #E5E7EB',
          }}>
            {columns.map((c) => <span key={c}>{c}</span>)}
          </div>
          {rows.map((row, i) => (
            <div
              key={`${row[0]}-${row[1]}-${i}`}
              style={{
                display: 'grid', gridTemplateColumns: '0.9fr 1.4fr 0.5fr', gap: 8,
                padding: '10px 0', borderBottom: '1px solid #F1F2F4',
                fontSize: 13, color: '#1A1F2B',
              }}
            >
              <span style={{ fontWeight: 600 }}>{row[0]}</span>
              <span>{row[1]}</span>
              <span style={{ fontWeight: 800, color: '#0F2557' }}>{row[2]}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
